import onlyOnceRunner from '@/core/concurrency/onlyOnceRunner';
import { authFetcher } from '@/core/http';
import { useImmutableAuthSWR } from '@/core/http/useAuthSWR';
import {
  trackCompletedEvent,
  trackFailedEvent,
  trackStartedEvent,
} from '@/core/monitoring/trackEvents';
import { usePaymentService } from '@/layout/appWrapper/ServiceProvider';
import settingsState from '@/stores/settingsStore';
import {
  monetizationOverrideState,
  MonetizationOverride,
} from '@/stores/monetizationOverrideStore';
import { hasSecretMenuSession } from '@/services/secretMenuSessionService';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { PaymentStrategiesIds } from '../paymentService';
import { UserFromBE } from './userTypes';

// SWR fetcher key
type UserKey = [string, string];

type UserFetcherProps = {
  onSuccess: CallableFunction;
  onError: CallableFunction;
  onStart: CallableFunction;
};

// Transform user fields from BE to FE.
export const userFetcher =
  ({ onStart, onSuccess, onError }: UserFetcherProps) =>
  ([url, token]: UserKey) => {
    onStart();
    return authFetcher([url, token])
      .then((data: UserFromBE) => {
        onSuccess();
        return {
          ...data,
          id: data.firebaseUid,
          webUserId: data.userId,
          email: data.email ?? undefined,
          giftCode: {
            ...data.giftCode,
            expiryAt: data.giftCode.expiryAt
              ? new Date(data.giftCode.expiryAt)
              : undefined,
          },
          monetization: {
            ...data.monetization,
            subscriptionIncludesTrial:
              !!data.monetization.subscriptionIncludesTrial,
            subscriptionWithIntroPrice:
              !!data.monetization.subscriptionWithIntroPrice,
          },
          createdAt: new Date(data.createdAt),
          nextRenewImageDownloadLimitsDatetime:
            data.nextRenewImageDownloadLimitsDatetime
              ? new Date(data.nextRenewImageDownloadLimitsDatetime)
              : undefined,
          marketingConsent: !!data.marketingConsent,
          imageTrainingConsent: !!data.imageTrainingConsent,
          isFree: !!data.isFree,
        } as const;
      })
      .catch((e) => {
        onError(e);
        throw e;
      });
  };

// Infer correct types to SWR (also needed for "mutate")
export type SWRUser = Awaited<ReturnType<ReturnType<typeof userFetcher>>>;

// Infer correct types to exported "data". When refactor is complete, we'll export this type only.
export type NewUserType = SWRUser & {
  paymentStrategy?: PaymentStrategiesIds;
};

const trackUserStart = onlyOnceRunner(() =>
  trackStartedEvent(['setup', 'user'])
);
const trackUserError = onlyOnceRunner((e: Error) =>
  trackFailedEvent(['setup', 'user'], { error: e.message })
);
const trackUserSuccess = onlyOnceRunner(() =>
  trackCompletedEvent(['setup', 'user'])
);

export function useUser() {
  const { getUserPlanFromPriceId } = usePaymentService();

  const resp = useImmutableAuthSWR<SWRUser>(
    '/users/@me',
    userFetcher({
      onStart: trackUserStart,
      onError: trackUserError,
      onSuccess: trackUserSuccess,
    })
  );

  const settings = useRecoilValue(settingsState);
  /* The stored override is honored only within an authenticated secret-menu
   * session (sessionStorage), so a stale value cannot affect the app. */
  const storedOverride = useRecoilValue(monetizationOverrideState);
  const monetizationOverride = hasSecretMenuSession() ? storedOverride : 'none';

  /* The plan is derived from the (possibly overridden) priceId, so a
   * simulated user does not keep the real subscription tier. */
  const userPaymentStrategy = getUserPlanFromPriceId(
    settings.values.paymentStrategies,
    resp.data?.monetization.priceId || ''
  );

  const overriddenUser = resp.data
    ? {
        ...resp.data,
        ...applyMonetizationOverride(resp.data, monetizationOverride),
      }
    : undefined;

  const user: NewUserType | undefined = useMemo(
    () =>
      overriddenUser && {
        ...overriddenUser,
        paymentStrategy:
          (monetizationOverride === 'none' ? userPaymentStrategy : '') ||
          undefined,
      },
    [overriddenUser, userPaymentStrategy, monetizationOverride]
  );

  return { ...resp, data: user };
}

/* Secret-menu monetization simulation: override the premium/free state of
 * the user without changing any server-side data. Only UI gates are
 * affected; server-enforced entitlements still reflect the real state. */
function applyMonetizationOverride(
  user: SWRUser,
  override: MonetizationOverride
): Partial<Pick<SWRUser, 'isFree' | 'monetization' | 'giftCode'>> {
  switch (override) {
    case 'premium':
      return {
        isFree: true,
        giftCode: { ...user.giftCode, isActive: false },
        monetization: {
          ...user.monetization,
          isSubscribed: true,
          subscriptionProvider: null,
          subscriptionId: null,
          priceId: undefined,
        },
      };
    case 'free':
      /* Every premium signal must be cleared: isFree marks a premium-tier
       * cohort (isPremiumUser treats it as premium), and an active gift code
       * also grants premium. A base user has neither plus no subscription. */
      return {
        isFree: false,
        giftCode: { ...user.giftCode, isActive: false },
        monetization: {
          ...user.monetization,
          isSubscribed: false,
          subscriptionProvider: null,
          subscriptionId: null,
          priceId: undefined,
        },
      };
    default:
      return {};
  }
}
